import { Fragment, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid, Typography } from '@breathelife/mui';
import { SimpleCheckbox } from '@breathelife/ui-components';
import { BlueprintCollectionOperator, BlueprintCollectionOperators } from '@zinnia/form-engine-sdk';

import { DEFAULT_COLLECTION_OPERATOR } from '../../../Helpers/conditions/blueprintHelpers';
import { ConditionPropertiesUpdateCallback } from '../types';
import { CollectionAggregateOperatorRadioGroup } from './CollectionAggregateOperatorRadioGroup';

type Props = {
  collectionContext: string[];
  collectionNodeIds: string[];
  collectionOperators: Record<string, BlueprintCollectionOperator> | undefined;
  onConditionPropertiesChange: ConditionPropertiesUpdateCallback;
  conditionIndex: string;
};

export function CollectionOperators({
  collectionNodeIds,
  collectionContext,
  collectionOperators,
  onConditionPropertiesChange,
  conditionIndex,
}: Props): ReactElement {
  const { t } = useTranslation();

  return (
    <Fragment>
      {collectionNodeIds.map((collectionNodeId) => {
        const isCollectionInContext = collectionContext.includes(collectionNodeId);

        const isUsingCollectionContext =
          !collectionOperators?.[collectionNodeId] ||
          collectionOperators?.[collectionNodeId] === BlueprintCollectionOperators.thisItem;

        return (
          <Fragment key={collectionNodeId}>
            <Grid item xs={12}>
              <Typography variant='h3' component='h1'>
                {t('admin.conditions.labels.selectedCollection', { collectionNodeId })}
              </Typography>
            </Grid>
            {isCollectionInContext && (
              <Grid item xs={12}>
                <SimpleCheckbox
                  id={`useLocalContext-${collectionNodeId}-${conditionIndex}`}
                  label={t('admin.conditions.labels.collectionCompareToCurrentItem')}
                  checked={isUsingCollectionContext}
                  onChange={(event) => {
                    const newCollectionOperator = event.target.checked
                      ? BlueprintCollectionOperators.thisItem
                      : DEFAULT_COLLECTION_OPERATOR;

                    const updatedCollectionOperators: Record<string, BlueprintCollectionOperator> = {
                      ...collectionOperators,
                      [collectionNodeId]: newCollectionOperator,
                    };
                    onConditionPropertiesChange({ collectionOperators: updatedCollectionOperators });
                  }}
                />
              </Grid>
            )}
            {(!isUsingCollectionContext || !isCollectionInContext) && (
              <Grid item xs={12} key={collectionNodeId}>
                <CollectionAggregateOperatorRadioGroup
                  value={collectionOperators?.[collectionNodeId] ?? DEFAULT_COLLECTION_OPERATOR}
                  onChange={(value) => {
                    const updatedCollectionOperators: Record<string, BlueprintCollectionOperator> = {
                      ...collectionOperators,
                      [collectionNodeId]: value,
                    };
                    onConditionPropertiesChange({ collectionOperators: updatedCollectionOperators });
                  }}
                />
              </Grid>
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
}
